import { Ajax } from "../../api/infra";
import { Dates } from "../../util";
import { CalculationRowSource } from "../../model";
import type { Calculation, PayrollDetails, PayrollListItem } from "../../model";
import type { InputEnumOption } from "../../ui";
import { WorktimeImportStatus } from "./model";
import type { WorktimeImport, WorktimeImportRow, WorktimeImportInStagingListItem } from "./model";
import { StagingApiConnector } from "./StagingApiConnector";

/** Connects to Worktime service in the staging server */
export class WorktimeApiConnector extends StagingApiConnector<WorktimeImport, WorktimeImportInStagingListItem> {

  constructor(serviceUrl: string, ajax: Ajax) {
    super(serviceUrl, ajax, WorktimeApiConnector.getBlank);
  }

  /**
   * If a payroll is selected, filters the list of worktime imports to only show those with calculations for the selected payroll.
   */
  public get filteredList(): WorktimeImportInStagingListItem[] {
    if (!this.payrollId) {
      return this.list;
    }
    return this.list.filter(x => x.payrolls[this.payrollId]);
  }

  /** The current rows filtered by current payroll */
  public filteredRows: WorktimeImportRow[] = [];

  /** Manually editable rows */
  public editableRows: WorktimeImportRow[] = [];

  /** Number of rows that are outside the already created Payrolls. */
  public rowsOutsidePayrollCount: number = 0;

  /**
   * List of payrolls available in the system
   * Undefined if the payrolls have not yet been fetched.
   */
  public payrolls: PayrollListItem[] | undefined = undefined;

  /** Currently selected Payroll */
  public payroll: PayrollDetails | undefined = undefined;

  /** Current payroll id if the payroll filter has been set. */
  public get payrollId(): string | undefined {
    return this.payroll?.id;
  }

  /** Current calc is the main preview calculation if available. */
  public currentCalc: Calculation | undefined;

  /** All preview calculations as input enum bindable array */
  public calculations: InputEnumOption[] = [];

  /** Getter and setter for current calculation by ID. */
  public get currentCalcId(): string | undefined {
    return this.currentCalc?.id;
  }
  public set currentCalcId(value: string | undefined) {
    this.currentCalc = this.current?.preview?.calculations.find(x => x.id == value);
    this.setFiltersByCurrentCalc();
  }

  /**
   * Set the current payroll
   * @param payroll The payroll to set as current.
   */
  public setPayroll(payroll: PayrollDetails | undefined): void {
    this.payroll = payroll;
    this.setcurrentCalcByPayrollId();
  }

  /**
   * Marks the selected rows with given state.
   * @param rows - The rows to mark.
   * @param status - The status to set.
   * Status "new" is essentially reverting the state from "ignored" => final state may be "preview", "new" or "error" depending on other properties of the row.
   */
  public async setStatus(rows: WorktimeImportRow[], status: "ignored" | "new"): Promise<WorktimeImport> {
    if (!this.current?.id) {
      throw new Error("No current item to update status for.");
    }
    const result = await this.ajax.postJSON(this.serviceUrl + "/update-worktime-status", {
      id: this.current.id,
      status,
      rows,
    }) as WorktimeImport;
    return result;
  }

  /** Sets the current item in the UI. */
  public setCurrent(item: WorktimeImport | undefined): void {
    super.setCurrent(item);
    this.currentCalc = undefined;
    this.calculations = [];
    this.rowsOutsidePayrollCount = 0;
    if (!item?.preview?.calculations?.length) {
      this.setcurrentCalcByPayrollId();
      return;
    }
    this.calculations = item.preview.calculations.map((calc) => {
      // HACK: Why is period empty sometimes here?
      const rangeText = Dates.getFormattedRange(calc.info.workStartDate || this.current.header?.period?.start || "1900-01-01", calc.info.workEndDate || this.current.header?.period?.end|| "2100-01-01");
      return {
        value: calc.id,
        text: `${rangeText} - ${calc.workflow.salaryDate} (...${calc.id?.substring(calc.id.length - 6)})`,
        ui: {
          start: calc.info.workStartDate,
        }
      };
    });

    this.calculations = this.calculations.sort((a, b) => (b.ui?.start ?? "").localeCompare(a.ui?.start ?? ""));
    this.rowsOutsidePayrollCount = item.rows.filter(x => (x.data.payrollId || "unknown") == "unknown").length;
    if (this.rowsOutsidePayrollCount) {
      this.calculations.push({
        value: undefined,
        text: `Rivit palkkalistojen ulkopuolella (${this.rowsOutsidePayrollCount} kpl)`,
      });
    }
    this.setcurrentCalcByPayrollId();
  }

  /**
   * Client-side (synchronous) method for getting a new blank item as bases for UI binding.
   * TODO: Move to helpers.
   */
  public static getBlank(): WorktimeImport {
    return {
      rows: [],
      files: [],
      header: {
        period: {},
        status: WorktimeImportStatus.Uploaded,
      },
      usecase: {
        data: {},
      },
      workflowData: {
        events: [],
      },
    };
  }

  /**
   * Gets the payroll (list item) for the currently selected calculation.
   * This returns undefined if the list of payrolls has not been loaded (Payroll details view).
   * @returns The payroll metadata if found or undefined.
   */
  public getPayrollForCurrentCalc(): PayrollListItem | undefined {
    return this.payrolls?.find(x => x.id == this.currentCalc?.info.payrollId);
  }

  /**
   * Recalculates a worktime item in storage.
   * @param id Identifier of the worktime item to recalculate. If none, is specified recaculates the current (current is also set).
   * @returns The recalculated worktime item or undefined if not found / no current & id.
   */
  public async recalculateWorktime(id?: string): Promise<WorktimeImport | undefined> {
    const setCurrent = !id;
    id = id || this.current?.id;
    if (!id) {
      return undefined;
    }
    const result = await this.ajax.postJSON(this.serviceUrl + "/recalculate/" + id, {});
    if (setCurrent) {
      this.setCurrent(result);
    }
    return result;
  }

  /**
   * Saves the manual rows to the server.
   * @returns The updated worktime import.
   */
  public async saveManualRows(): Promise<void> {
    const payrollId = this.currentCalc?.info.payrollId;
    if (!payrollId) {
      throw new Error("No payroll selected for manual rows.");
    }
    this.current.rows = this.current.rows.filter(x => x.source != CalculationRowSource.ManualRow);
    this.editableRows.forEach(x => {
      x.employmentId = this.current.id;
      x.source = CalculationRowSource.ManualRow;
      x.status = "edited";
      x.data.payrollId = payrollId;
      x.period = x.period || {};
      x.period.start = x.period.start || this.currentCalc?.info.workStartDate;
      x.period.end = x.period.end || this.currentCalc?.info.workEndDate;
      this.current.rows.push(x);
    });
    this.setcurrentCalcByPayrollId();
    await this.saveCurrent();
  }

  private setcurrentCalcByPayrollId() {
    if (!this.current?.preview?.calculations?.length) {
      this.currentCalc = undefined;
      this.setFiltersByCurrentCalc();
      return;
    }
    if (this.payrollId) {
      this.currentCalc = this.current.preview.calculations.find(x => x.info.payrollId == this.payrollId);
    }
    if (!this.currentCalc) {
      this.currentCalc = this.current.preview.calculations[0];
    }
    this.setFiltersByCurrentCalc();
  }

  private setFiltersByCurrentCalc() {
    const payrollId = this.currentCalc?.info.payrollId || "unknown";
    this.filteredRows = (this.current?.rows || []).filter(x => x.data.payrollId == payrollId);
    this.editableRows = payrollId == "unknown" ? [] : this.filteredRows.filter(x => x.source == CalculationRowSource.ManualRow);
  }

}